/**
 * The lock screen. FR-111, S-02.
 *
 * Shows NOTHING. No last-used date, no cycle day, no preview, no count of
 * entries. Someone holding the phone learns only that an app called Thana wants
 * a PIN — and the neutral name means even that says little (FR-115).
 */
import { useState } from 'react'
import { t } from '@copy/index'
import { useLock } from '@contexts/index'
import { Notice } from '@shared/ui'

export function LockScreen() {
  const { unlock, lastAttemptFailed } = useLock()
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (pin === '' || busy) return
    setBusy(true)
    // Key derivation is deliberately slow (600,000 iterations), so this takes
    // about a second on a phone. The button says so rather than appearing stuck.
    const ok = await unlock(pin)
    setBusy(false)
    if (!ok) setPin('')
  }

  return (
    <main className="flex h-full flex-col items-center justify-center gap-4 p-6">
      <h1 className="text-lg font-medium">{t('lock.title')}</h1>

      <form onSubmit={(event) => void submit(event)} className="flex w-full max-w-xs flex-col gap-3">
        <label htmlFor="pin" className="text-sm">
          {t('lock.prompt')}
        </label>
        <input
          id="pin"
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          autoFocus
          value={pin}
          onChange={(event) => setPin(event.target.value)}
          className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 text-center text-lg tracking-widest transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
        />
        <button
          type="submit"
          disabled={busy || pin === ''}
          className="min-h-11 rounded-lg bg-accent text-accent-ink disabled:opacity-50 transition-all duration-200 active:scale-[0.98] hover:opacity-90"
        >
          {busy ? t('lock.unlocking') : t('lock.unlock')}
        </button>
      </form>

      {lastAttemptFailed ? (
        <div className="w-full max-w-xs">
          {/* No distinction between "wrong PIN" and any other failure: a message
              confirming the PIN was merely wrong is a slightly better oracle for
              someone guessing. */}
          <Notice tone="error">{t('lock.failed')}</Notice>
        </div>
      ) : null}

      <p className="max-w-xs text-center text-xs text-ink-muted">{t('lock.noRecovery')}</p>
      <p className="max-w-xs text-center text-xs text-ink-muted mt-2">{t('lock.syncPaused')}</p>
    </main>
  )
}
